import _ from 'lodash';
import { canadianProvincesMap } from 'shared/core/constants/geography';
import { getComplianceConfig } from 'shared/core/helpers/compliance';
import {
  MILLIGRAMS_PER_GRAM,
  GRAMS_PER_OUNCE,
  FLOWER_TYPES,
  CONCENTRATE_TYPES,
  EDIBLE_TYPES,
  TINCTURE_TYPES,
  TOPICAL_TYPES,
  WEIGHTED_TYPES,
} from './constants';

export const getStateLimits = (customerState, dispensaryState) => {
  const outOfStateLimits = getComplianceConfig(customerState, 'outOfStateLimits');
  let limits = getComplianceConfig(customerState, 'limits');
  if (outOfStateLimits && dispensaryState && dispensaryState !== customerState) {
    limits = outOfStateLimits;
  }
  return limits;
};

export const getTypeLimits = (cart) => {
  const { limits, medical } = cart;
  const key = medical ? 'medical' : 'rec';
  const flowerLimit = limits.Flower?.[key] || Infinity;
  const concentrateLimit = limits.Concentrate?.[key] || Infinity;
  const edibleTHCLimit = limits.EdibleTHC?.[key] || Infinity;
  const tinctureLimit = limits.TinctureTHC?.[key] || Infinity;
  const topicalLimit = limits.TopicalTHC?.[key] || Infinity;
  const solidEdibleNetWeightLimit = limits.SolidEdibleNetWeight?.[key] || Infinity;
  const liquidEdibleVolumeLimitInOunces = limits.LiquidEdibleVolumeInOunces?.[key] || Infinity;

  return {
    flowerLimit,
    concentrateLimit,
    edibleTHCLimit,
    tinctureLimit,
    topicalLimit,
    solidEdibleNetWeightLimit,
    liquidEdibleVolumeLimitInOunces,
  };
};

export const isFlower = (type) => _.includes(FLOWER_TYPES, type);
export const isConcentrate = (type) => _.includes(CONCENTRATE_TYPES, type);
export const isEdible = (type) => _.includes(EDIBLE_TYPES, type);
export const isTincture = (type) => _.includes(TINCTURE_TYPES, type);
export const isTopical = (type) => _.includes(TOPICAL_TYPES, type);
export const isWeightedProduct = ({ type }) => _.includes(WEIGHTED_TYPES, type);

export const isLiquidTypeProduct = (limits, type, subcategory) => {
  const liquidCategories = limits.liquidEdibleTypes?.categories ?? [];
  const liquidSubcategories = limits.liquidEdibleTypes?.subcategories ?? [];

  const liquidTypes = [...liquidCategories, ...liquidSubcategories];

  return _.includes(liquidTypes, type) || _.includes(liquidTypes, subcategory);
};

export const isSolidEdibleTypeProduct = (type, subcategory) => isEdible(type) && subcategory !== 'drinks';

export const getLimitForType = ({ type: productType, subcategory: productSubcategory }, cart) => {
  const { limits } = cart;
  const {
    flowerLimit,
    concentrateLimit,
    edibleTHCLimit,
    tinctureLimit,
    topicalLimit,
    solidEdibleNetWeightLimit,
    liquidEdibleVolumeLimitInOunces,
  } = getTypeLimits(cart);

  const currentType = narrowProductType(productType, productSubcategory, limits);

  const typeMap = {
    Flower: flowerLimit,
    Concentrate: concentrateLimit,
    Edible: limits.usesSolidEdibleTypes ? solidEdibleNetWeightLimit : edibleTHCLimit,
    Tincture: tinctureLimit,
    Topical: topicalLimit,
    Liquid: liquidEdibleVolumeLimitInOunces,
  };

  return typeMap[currentType];
};

export const formatDisplayedType = (type) => {
  const typeMap = {
    Liquid: 'liquids',
    liquids: 'liquids',
    liquid: 'liquids',
    Tincture: 'tinctures',
    tintures: 'tinctures',
    tincture: 'tinctures',
    seeds: 'seeds',
    Edible: 'edibles',
    edibles: 'edibles',
    edible: 'edibles',
    Topical: 'topicals',
    topicals: 'topicals',
    topical: 'topicals',
    Flower: 'flower',
    flower: 'flower',
    'Pre-Rolls': 'flower',
    'pre-rolls': 'flower',
    cannabis: 'cannabis',
    Cannabis: 'cannabis',
    Concentrate: 'concentrates',
    concentrate: 'concentrates',
    Concentrates: 'concentrates',
    concentrates: 'concentrates',
    drinks: 'drinks',
  };

  return typeMap[type];
};

export const getLimitMessage = (type, limit, state, unit = 'g') => {
  const inCanada = _.has(canadianProvincesMap, state);

  const message = (msgLimit, msgUnit) =>
    // eslint-disable-next-line max-len
    `Sorry! You've reached the ${msgLimit}${msgUnit} purchase limit for ${type} due to ${
      inCanada ? 'federal' : 'state'
    } regulations.`;

  // if unit is milligrams, convert gram limit to milligrams
  if (unit === 'mg') {
    return message((limit *= MILLIGRAMS_PER_GRAM), unit);
  }

  // if converting to oz results in a clean number gte to 1, show ozLimit
  const ozLimit = limit / GRAMS_PER_OUNCE;
  if (Math.round(ozLimit * 100) === ozLimit * 100 && ozLimit >= 1) {
    return message(ozLimit, 'oz');
  }

  return message(limit, unit);
};

export const narrowProductType = (type, subcategory = '', limits = {}) => {
  if (isFlower(type)) {
    return 'Flower';
  }

  if (isConcentrate(type)) {
    return 'Concentrate';
  }

  if (isLiquidTypeProduct(limits, type, subcategory)) {
    return 'Liquid';
  }

  if (isEdible(type) || isSolidEdibleTypeProduct(type, subcategory)) {
    return 'Edible';
  }

  if (isTincture(type)) {
    return 'Tincture';
  }

  if (isTopical(type)) {
    return 'Topical';
  }

  return type;
};

export const productIncludedInType = (product, type, order) => {
  if (!type || !order) {
    return false;
  }

  const { type: category, subcategory } = product;
  const { customerState, dispensary } = order;
  const limits = getStateLimits(customerState, dispensary?.location?.state);
  const { typeMap, ignoreTypeMap } = limits;
  let defaultsForType;

  switch (type) {
    case 'Flower':
      defaultsForType = FLOWER_TYPES;
      break;
    case 'Concentrate':
      defaultsForType = CONCENTRATE_TYPES;
      break;
    case 'Edible':
      defaultsForType = EDIBLE_TYPES;
      break;
    case 'Tincture':
      defaultsForType = TINCTURE_TYPES;
      break;
    case 'Topicals':
      defaultsForType = TOPICAL_TYPES;
      break;
    default:
      defaultsForType = [type];
  }

  const ignoredCategoriesForType = ignoreTypeMap?.[type]?.categories ?? [];
  const ignoredSubCategoriesForType = ignoreTypeMap?.[type]?.subcategories ?? [];

  const includedCategoriesForType = typeMap?.[type]?.categories ?? [];
  const includedSubCategoriesForType = typeMap?.[type]?.subcategories ?? [];

  // Only returns true if:
  // the product's category OR subcategory is NOT in the ignoreTypeMap overrides for the specified type, and...
  // the product's category OR subcategory is in the typeMap overrides or defaults for the specified type, or...
  // the product's category is the specified type

  const isIgnoredCategory = _.includes(ignoredCategoriesForType, category);
  const isIgnoredSubCategory = _.includes(ignoredSubCategoriesForType, subcategory);
  const isAddedCategory = _.includes(includedCategoriesForType, category);
  const isAddedSubCategory = _.includes(includedSubCategoriesForType, subcategory);

  if (isIgnoredCategory || isIgnoredSubCategory) {
    return false;
  }

  if (category !== type) {
    if (!isAddedCategory && !isAddedSubCategory && !_.includes(defaultsForType, category)) {
      return false;
    }
  }

  return true;
};
